import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRightLeft,
  ChevronDown,
  Clock,
  DollarSign,
  Package,
  ShieldCheck,
  UserCog,
  Users as UsersIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { RESTAURANT_BY_ID, ROLES, ROLE_LABEL } from "../constants";
import {
  clearSession,
  getCurrentUser,
  loadCategories,
  loadProducts,
  loadRequests,
  loadSuppliers,
  loadUsers,
} from "../lib/storage";
import {
  canCreateTransfer,
  canManageUsers,
  isMain,
  isRestMgr,
  visibleRequests,
} from "../lib/permissions";
import RequestsPanel from "../components/RequestsPanel";
import CatalogPanel from "../components/CatalogPanel";
import UsersPanel from "../components/UsersPanel";
import TransferDialog from "../components/TransferDialog";
import FinanceDashboard from "../components/finance/FinanceDashboard";
import { Button } from "../components/ui/button";

const ASSISTANT_SUGGESTIONS = [
  "What should I order today?",
  "Which requests are still pending?",
  "Summarize requests by supplier.",
  "Which products are unavailable?",
  "What deliveries are awaiting an invoice?",
  "Show me the Spring Mountain requests.",
];

export default function Dashboard({ role }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState("requests"); // requests | transfers | catalog | users | finance
  const [initialStatus, setInitialStatus] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [todayTasks, setTodayTasks] = useState([]);
  const [dashboardDate, setDashboardDate] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantInput, setAssistantInput] = useState("");
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState([
  {
    role: "assistant",
    content: "Good morning. How can I help with purchasing operations today?",
  },
]);

  useEffect(() => {
    const u = getCurrentUser();
    if (!u) { navigate("/"); return; }
    if (role === "main" && u.role !== ROLES.MAIN) { navigate("/"); return; }
    if (role === "restaurant" && u.role !== ROLES.RESTAURANT) { navigate("/"); return; }
    setUser(u);
    setProducts(loadProducts());
    setSuppliers(loadSuppliers());
    setCategories(loadCategories());
    setRequests(loadRequests());
    setUsers(loadUsers());
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/today`)
  .then((r) => r.json())
  .then((data) => {
  setDashboardDate(data.date || null);
  setTodayTasks(data.tasks || []);
  })
  .catch((err) => {
    console.error("Dashboard error:", err);
  });
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/briefing`)
  .then((r) => r.json())
  .then((data) => {
    setBriefing(data.briefing);
  })
  .catch((err) => {
    console.error("Briefing error:", err);
  });
  }, [navigate, role]);

  const scopedRequests = useMemo(() => visibleRequests(user, requests), [user, requests]);

  const stats = useMemo(() => {
    const pending = scopedRequests.filter((r) => r.status === "submitted" || r.status === "pending").length;
    const approved = scopedRequests.filter((r) => r.status === "approved").length;
    const awaitingInvoice = scopedRequests.filter(
      (r) => r.type === "purchase" && r.status === "approved" && !r.deliveryConfirmedAt
    ).length;
    const transfers = scopedRequests.filter((r) => r.type === "internal_transfer").length;
    return { pending, approved, awaitingInvoice, transfers, total: scopedRequests.length };
  }, [scopedRequests]);

  // Jumps the Requests tab to the invoice-confirmation flow.
  const goToAwaitingInvoice = () => {
    setView("requests");
    setInitialStatus("awaiting_delivery");
  };

  const handleSignOut = () => { clearSession(); navigate("/"); };
  const toggleTaskCompletion = async (task) => {
  if (!user) return;

  if (task.completed) {
    await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/tasks/complete/${task.id}?date=${dashboardDate}`,
      { method: "DELETE" }
    );
  } else {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        taskId: task.id,
        date: dashboardDate,
        completedById: user.id,
        completedByName: user.name,
      }),
    });
  }

  const updated = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/today`);
  const data = await updated.json();
  setDashboardDate(data.date || null);
  setTodayTasks(data.tasks || []);
};

  if (!user) return null;
  const sendAssistantMessage = async () => {
  const message = assistantInput.trim();

  if (!message || assistantLoading) return;

  const userMessage = {
    role: "user",
    content: message,
  };

  setAssistantMessages((current) => [...current, userMessage]);
  setAssistantInput("");
  setAssistantLoading(true);

  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/ai/ask`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message,
          userId: user?.id,
          userName: user?.name,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Assistant request failed: ${response.status}`);
    }

    const data = await response.json();

    setAssistantMessages((current) => [
      ...current,
      {
        role: "assistant",
        content:
          data.answer ||
          "I could not prepare an answer with the available information.",
      },
    ]);
  } catch (error) {
    console.error("JGI Assistant error:", error);

    setAssistantMessages((current) => [
      ...current,
      {
        role: "assistant",
        content:
          "I could not connect to the assistant right now. Please try again.",
      },
    ]);
  } finally {
    setAssistantLoading(false);
  }
};

  const restaurant = user.restaurant ? RESTAURANT_BY_ID[user.restaurant] : null;

  return (
    <div className="min-h-screen bg-[#F3EFE7] text-[#0B1F3A]">
      <header className="sticky top-0 z-30 bg-[#F3EFE7]/85 backdrop-blur border-b border-[#E6E0D4]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center gap-3">
          <button onClick={handleSignOut} className="h-9 w-9 rounded-full hover:bg-[#E9E3D6] flex items-center justify-center transition-colors" aria-label="Back">
            <ArrowLeft size={18} />
          </button>
          <img
            src="/brand-mark.png"
            alt="Café Breizh · JGI Stock Operation"
            className="h-9 w-9 rounded-lg object-cover shrink-0"
            data-testid="brand-mark"
          />
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center ${isMain(user) ? "bg-[#F7E4DB] text-[#C4552B]" : "bg-[#EDEAE2] text-[#0B1F3A]"}`}>
              {isMain(user) ? <ShieldCheck size={16} strokeWidth={1.8} /> : <UserCog size={16} strokeWidth={1.8} />}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] tracking-eyebrow uppercase text-[#0B1F3A]/55 truncate">{isMain(user) ? "Operations · Main" : `${restaurant?.brand} · ${restaurant?.name}`}</p>
              <p className="text-sm font-medium leading-tight truncate">{user.name} · {ROLE_LABEL[user.role]}</p>
            </div>
          </div>
          <div className="flex-1" />
          <div className="hidden md:flex items-center gap-2 text-xs text-[#0B1F3A]/60"><Clock size={14} /> Live · synced to iPads</div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-7">
        <div className="mb-5 fade-up">
          <p className="text-[11px] tracking-eyebrow uppercase text-[#0B1F3A]/55 mb-2">
            {view === "catalog" ? "Catalog" : view === "users" ? "User management" : view === "transfers" ? "Internal transfers" : "Purchasing board"}
          </p>
          <h1 className="font-serif-display text-3xl md:text-5xl leading-tight">
            {isMain(user) ? (
              <>All restaurants, <span className="italic text-[#C4552B]">every request</span>.</>
            ) : (
              <>{restaurant?.name}, <span className="italic text-[#C4552B]">today</span>.</>
            )}
          </h1>
        </div>
        {briefing && (
  <div className="bg-gradient-to-r from-[#0B1F3A] to-[#1F4A7A] rounded-2xl p-6 mb-6 text-white shadow-lg">
    <div className="flex items-start justify-between">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.2em] opacity-70">
          🧠 JGI Assistant
        </p>

        <h2 className="text-2xl font-semibold mt-2">
          Good morning, {user?.name}
        </h2>

        <p className="mt-3 text-white/90">
          {briefing.message}
        </p>

        <p className="mt-2 text-white/75">
          {briefing.summary}
        </p>
      </div>

        <button
  type="button"
  onClick={() => setAssistantOpen(true)}
  className="bg-white text-[#0B1F3A] px-5 py-2 rounded-xl font-semibold hover:bg-gray-100 transition"
>
  Open Assistant
</button>
</div>
</div>
)}
         {/* Today's tasks */}
{todayTasks.length > 0 && (
  <div className="bg-white border border-[#E6E0D4] rounded-2xl p-5 mb-6 shadow-sm">
    <p className="text-[11px] tracking-eyebrow uppercase text-[#0B1F3A]/55 mb-3">
      Today's tasks
    </p>

    <div className="space-y-2">
      {todayTasks.map((task) => (
        <div
          key={task.id}
          className="flex items-center justify-between gap-3 rounded-xl border border-[#E6E0D4] bg-[#F9F6EF] px-4 py-3"
        >
          <div className="flex items-start gap-3">

             <button
  type="button"
  onClick={() => toggleTaskCompletion(task)}
  className={`mt-0.5 h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 hover:scale-110 ${
    task.completed
      ? "bg-green-600 text-white hover:bg-green-700"
      : "border-2 border-gray-400 text-gray-400 hover:border-[#0B1F3A] hover:text-[#0B1F3A]"
  }`}
>
  {task.completed ? "✓" : ""}
</button>

            <div>
              <p className="text-sm font-medium text-[#0B1F3A]">
                {task.title}
              </p>

              <p className="text-xs text-[#0B1F3A]/55">
                {task.supplierName}
                {task.deliveryDay ? ` · Delivery ${task.deliveryDay}` : ""}
                {task.startTime ? ` · ${task.startTime}-${task.endTime}` : ""}
              </p>

              <p className="text-xs mt-1">
                {task.completed ? (
                  <span className="text-green-700">
                    ✓ {task.completedByName} ·{" "}
                    {new Date(task.completedAt).toLocaleTimeString([], {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                ) : (
                  <span className="text-amber-600">
                    ○ Pending
                  </span>
                )}
              </p>
            </div>

          </div>

          <span className="text-xs rounded-full bg-[#0B1F3A] text-white px-3 py-1">
            #{task.priority}
          </span>
        </div>
      ))}
    </div>
  </div>
)}
        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Stat label="Submitted" value={stats.pending} />
          <Stat label="Approved" value={stats.approved} tone="approved" />
          <Stat
            label="Awaiting invoice"
            value={stats.awaitingInvoice}
            tone="amber"
            onClick={stats.awaitingInvoice > 0 ? goToAwaitingInvoice : undefined}
            testId="stat-awaiting-invoice"
          />
          <Stat label="Transfers" value={stats.transfers} />
          <Stat label="Total visible" value={stats.total} />
        </div>

        {/* View tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="inline-flex rounded-lg bg-white border border-[#E6E0D4] p-1">
            <ViewTab testKey="requests" active={view==="requests"} onClick={()=>setView("requests")}><Package size={14} /> Requests</ViewTab>
            <ViewTab testKey="transfers" active={view==="transfers"} onClick={()=>setView("transfers")}><ArrowRightLeft size={14} /> Transfers</ViewTab>
            <ViewTab testKey="catalog" active={view==="catalog"} onClick={()=>setView("catalog")}><Package size={14} /> Catalog</ViewTab>
            {isMain(user) && <ViewTab testKey="finance" active={view==="finance"} onClick={()=>setView("finance")}><DollarSign size={14} /> Finance</ViewTab>}
            {canManageUsers(user) && <ViewTab testKey="users" active={view==="users"} onClick={()=>setView("users")}><UsersIcon size={14} /> Users</ViewTab>}
          </div>
          <div className="flex-1" />
          {canCreateTransfer(user) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="new-menu-trigger" className="bg-[#0B1F3A] hover:bg-[#11294A] text-white">New <ChevronDown size={14} className="ml-1" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="new-transfer-btn" onClick={() => setTransferOpen(true)}><ArrowRightLeft size={14} className="mr-2" /> Internal transfer</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {view === "requests" && (
          <RequestsPanel user={user} requests={scopedRequests} products={products} suppliers={suppliers} users={users} onRequestsChange={setRequests} scope="purchase" initialStatus={initialStatus} />
        )}
        {view === "transfers" && (
          <RequestsPanel user={user} requests={scopedRequests} products={products} suppliers={suppliers} users={users} onRequestsChange={setRequests} scope="transfer" />
        )}
        {view === "catalog" && (
          <CatalogPanel user={user} products={products} suppliers={suppliers} categories={categories} onProductsChange={setProducts} onSuppliersChange={setSuppliers} onCategoriesChange={setCategories} />
        )}
        {view === "finance" && isMain(user) && (
          <FinanceDashboard user={user} requests={scopedRequests} products={products} suppliers={suppliers} awaitingInvoice={stats.awaitingInvoice} onGoToRequests={goToAwaitingInvoice} />
        )}
        {view === "users" && canManageUsers(user) && (
          <UsersPanel currentUser={user} />
        )}

        {isRestMgr(user) && (
          <p className="mt-8 text-xs text-[#0B1F3A]/50">
            You're scoped to <strong>{restaurant?.name}</strong>. Purchase orders and transfers flow without needing the main manager.
          </p>
        )}
      </main>
{assistantOpen && (
  <div className="fixed inset-0 z-50">
    <button
      type="button"
      aria-label="Close assistant"
      onClick={() => setAssistantOpen(false)}
      className="absolute inset-0 bg-[#0B1F3A]/35 backdrop-blur-[2px]"
    />

    <aside
      className="absolute right-0 top-0 h-[100dvh] w-full sm:w-[430px] bg-[#F9F6EF] shadow-2xl flex flex-col overflow-hidden"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div className="px-5 py-4 border-b border-[#E6E0D4] bg-[#0B1F3A] text-white flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center text-lg">
          🧠
        </div>

        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/65">
            Purchasing Operations
          </p>
          <h2 className="text-lg font-semibold">JGI Assistant</h2>
        </div>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setAssistantOpen(false)}
          className="h-9 w-9 rounded-full hover:bg-white/10 text-xl"
          aria-label="Close assistant"
        >
          ×
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
        {assistantMessages.length === 1 && (
          <div className="rounded-2xl border border-[#E6E0D4] bg-white/70 p-3">
            <p className="mb-2 text-[10px] uppercase tracking-[0.18em] text-[#0B1F3A]/55">
              Try asking
            </p>
            <div className="flex flex-wrap gap-2">
              {ASSISTANT_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setAssistantInput(suggestion)}
                  disabled={assistantLoading}
                  className="rounded-full border border-[#D8D1C4] bg-white px-3 py-2 text-left text-xs text-[#0B1F3A] transition-colors hover:border-[#0B1F3A] hover:bg-[#F3EFE7] disabled:opacity-40"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {assistantMessages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            className={`flex ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                message.role === "user"
                  ? "bg-[#0B1F3A] text-white rounded-br-md"
                  : "bg-white border border-[#E6E0D4] text-[#0B1F3A] rounded-bl-md shadow-sm"
              }`}
            >
              {message.content}
            </div>
          </div>
        ))}

        {assistantLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-[#E6E0D4] rounded-2xl rounded-bl-md px-4 py-3 text-sm text-[#0B1F3A]/60">
              JGI Assistant is reviewing the operation...
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-[#E6E0D4] bg-white p-4">
        <div className="flex items-end gap-2">
          <textarea
            value={assistantInput}
            onChange={(event) => setAssistantInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                sendAssistantMessage();
              }
            }}
            rows={2}
            placeholder="Ask about tasks, requests, suppliers..."
            className="flex-1 resize-none rounded-xl border border-[#D8D1C4] bg-[#F9F6EF] px-3 py-2.5 text-sm outline-none focus:border-[#0B1F3A]"
          />

          <button
            type="button"
            onClick={() => sendAssistantMessage()}
            disabled={!assistantInput.trim() || assistantLoading}
            className="h-11 px-4 rounded-xl bg-[#0B1F3A] text-white text-sm font-semibold disabled:opacity-40"
          >
            Send
          </button>
        </div>

        <p className="mt-2 text-[10px] text-[#0B1F3A]/45">
          Answers are based on Café Breizh purchasing data.
        </p>
      </div>
    </aside>
  </div>
)}
      <TransferDialog open={transferOpen} onOpenChange={setTransferOpen} user={user} products={products} onCreated={() => setRequests(loadRequests())} />
    </div>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "approved" ? "text-[#2B7A6B]" : "text-[#0B1F3A]";
  return (
    <div className="bg-white border border-[#E6E0D4] rounded-xl px-4 py-4">
      <p className="text-[10px] tracking-eyebrow uppercase text-[#0B1F3A]/55">{label}</p>
      <p className={`font-serif-display text-3xl mt-1 ${color}`}>{value}</p>
    </div>
  );
}

function ViewTab({ active, onClick, children, testKey }) {
  return (
    <button
      onClick={onClick}
      data-testid={testKey ? `view-tab-${testKey}` : undefined}
      className={`h-9 px-3 rounded-md text-sm flex items-center gap-1.5 transition-colors ${active ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/75 hover:bg-[#F3EFE7]"}`}
    >
      {children}
    </button>
  );
}
